import { useAppSelector } from '@/store/store';
import type { BrokerAggregateStats, BrokerMeta, BrokerMetricsObjects } from '@/types/main';
import { useMemo } from 'react';

export function useBrokerByCode(): Map<string, BrokerMeta> {
    const brokers = useAppSelector((state) => state.brokerMetrics.brokers);
    return useMemo(() => new Map(brokers.map((broker) => [broker.code, broker])), [brokers]);
}

// The active tab's compared-broker data, keyed by broker code - only
// includes brokers whose data has actually loaded for this tab.
export function useActiveTabBrokerMetricsMap(): Record<string, BrokerMetricsObjects> {
    const comparedBrokers = useAppSelector((state) => state.brokerMetrics.comparedBrokers);
    const metricsCache = useAppSelector((state) => state.brokerMetrics.tabs[state.brokerMetrics.selectedEndpoint]?.metricsCache);
    const brokerByCode = useBrokerByCode();

    return useMemo(() => {
        const cache = metricsCache ?? {};
        return comparedBrokers.reduce(
            (acc, broker) => {
                if (cache[broker]) {
                    const meta = brokerByCode.get(broker);
                    acc[broker] = {
                        metrics: cache[broker],
                        color: meta?.color ?? '#64748B',
                        displayName: meta?.display_name ?? broker,
                    };
                }
                return acc;
            },
            {} as Record<string, BrokerMetricsObjects>
        );
    }, [comparedBrokers, metricsCache, brokerByCode]);
}

// The active tab's compared-broker server-aggregated stats, keyed by broker
// code - what the "Numbers" table renders, as opposed to the raw per-bucket
// data the chart uses.
export function useActiveTabBrokerStatsMap(): Record<string, { stats: BrokerAggregateStats; color: string; displayName: string }> {
    const comparedBrokers = useAppSelector((state) => state.brokerMetrics.comparedBrokers);
    const statsCache = useAppSelector((state) => state.brokerMetrics.tabs[state.brokerMetrics.selectedEndpoint]?.statsCache);
    const brokerByCode = useBrokerByCode();

    return useMemo(() => {
        const cache = statsCache ?? {};
        return comparedBrokers.reduce(
            (acc, broker) => {
                if (cache[broker]) {
                    const meta = brokerByCode.get(broker);
                    acc[broker] = {
                        stats: cache[broker],
                        color: meta?.color ?? '#64748B',
                        displayName: meta?.display_name ?? broker,
                    };
                }
                return acc;
            },
            {} as Record<string, { stats: BrokerAggregateStats; color: string; displayName: string }>
        );
    }, [comparedBrokers, statsCache, brokerByCode]);
}
